import React from 'react';
import { TestTube, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import type { EmailConfig, TestResults, ShowPasswords } from '../types';

interface EmailSettingsTabProps {
  config: EmailConfig;
  onChange: (config: EmailConfig) => void;
  testResults: TestResults;
  onTest: (service: string) => void;
  showPasswords: ShowPasswords;
  onTogglePassword: (field: string) => void;
}

const EmailSettingsTab: React.FC<EmailSettingsTabProps> = ({
  config,
  onChange,
  testResults,
  onTest,
  showPasswords,
  onTogglePassword
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de Email</h3>

        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="radio"
              id="sendgrid"
              name="emailProvider"
              checked={!config.useInternalEmail}
              onChange={() => onChange({ ...config, useInternalEmail: false })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <label htmlFor="sendgrid" className="text-sm font-medium text-gray-900">
              SendGrid (Recomendado)
            </label>
          </div>

          {!config.useInternalEmail && (
            <div className="ml-7 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SendGrid API Key *
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.sendgrid ? 'text' : 'password'}
                    value={config.sendgridApiKey}
                    onChange={(e) => onChange({ ...config, sendgridApiKey: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="SG...."
                  />
                  <button
                    type="button"
                    onClick={() => onTogglePassword('sendgrid')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.sendgrid ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                {testResults.sendgrid !== undefined && (
                  <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
                    testResults.sendgrid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {testResults.sendgrid ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span className="text-sm">{testResults.sendgrid ? 'Conectado' : 'Error'}</span>
                  </div>
                )}
                <button
                  onClick={() => onTest('sendgrid')}
                  className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <TestTube className="w-4 h-4" />
                  <span>Probar</span>
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-3">
            <input
              type="radio"
              id="smtp"
              name="emailProvider"
              checked={config.useInternalEmail}
              onChange={() => onChange({ ...config, useInternalEmail: true })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <label htmlFor="smtp" className="text-sm font-medium text-gray-900">
              SMTP Personalizado
            </label>
          </div>

          {config.useInternalEmail && (
            <div className="ml-7 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Servidor SMTP *</label>
                <input
                  type="text"
                  value={config.smtpHost}
                  onChange={(e) => onChange({ ...config, smtpHost: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="smtp.gmail.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Puerto *</label>
                <input
                  type="number"
                  value={config.smtpPort}
                  onChange={(e) => onChange({ ...config, smtpPort: parseInt(e.target.value) || 587 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="587"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usuario *</label>
                <input
                  type="text"
                  value={config.smtpUser}
                  onChange={(e) => onChange({ ...config, smtpUser: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="usuario@gmail.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
                <div className="relative">
                  <input
                    type={showPasswords.smtp ? 'text' : 'password'}
                    value={config.smtpPassword}
                    onChange={(e) => onChange({ ...config, smtpPassword: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => onTogglePassword('smtp')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.smtp ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="smtpSecure"
                    checked={config.smtpSecure}
                    onChange={(e) => onChange({ ...config, smtpSecure: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="smtpSecure" className="ml-2 block text-sm text-gray-900">
                    Usar conexión segura (TLS)
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Configuración del remitente</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email del remitente
                </label>
                <input
                  type="email"
                  value={config.emailFromAddress}
                  onChange={(e) => onChange({ ...config, emailFromAddress: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del remitente
                </label>
                <input
                  type="text"
                  value={config.emailFromName}
                  onChange={(e) => onChange({ ...config, emailFromName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSettingsTab;
